import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IndianRupee } from "lucide-react";

const vehicleModels = [
  "Kinetic Safar Smart",
  "Kinetic Safar Jumbo",
  "Kinetic Safar Star",
  "Kinetic Safar Shakti",
  "Kinetic E-Luna",
  "Kinetic Zing HSS",
  "Kinetic Safar Jumri",
  "Kinetic Safar City",
];

const vehicleColors = [
  "White",
  "Red",
  "Blue",
  "Green",
  "Yellow",
  "Black",
  "Silver",
  "Orange",
  "Grey",
];

// ── Helpers outside component so React never remounts the inputs ──
const handlePriceKeyDown = (e) => {
  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl) return;
  const pass = ["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"];
  if (pass.includes(e.key)) return;
  if (e.key === "." && !e.target.value.includes(".")) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};

const handlePhoneKeyDown = (e) => {
  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl) return;
  const pass = ["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"];
  if (pass.includes(e.key)) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};
// ─────────────────────────────────────────────────────────────────────────────

const QuotationForm = ({ formData, onInputChange, onSelectChange, calculateTotal }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };
  
  return (
    <div className="space-y-8">
      {/* Document Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Document Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="quotationNumber">Quotation Number</Label>
            <Input
              id="quotationNumber"
              name="quotationNumber"
              value={formData.quotationNumber}
              onChange={onInputChange}
              className="bg-secondary/30"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="quotationDate">Quotation Date</Label>
            <Input
              id="quotationDate"
              name="quotationDate"
              type="date"
              value={formData.quotationDate}
              onChange={onInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="validUntil">Valid Until</Label>
            <Input
              id="validUntil"
              name="validUntil"
              type="date"
              value={formData.validUntil}
              onChange={onInputChange}
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Customer Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="customerName">Customer Name (Mr./Mrs./Ku.) *</Label>
            <Input
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={onInputChange}
              placeholder="Enter customer name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerPhone">Mobile Number * (10 digits)</Label>
            <Input
              id="customerPhone"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={onInputChange}
              onKeyDown={handlePhoneKeyDown}
              placeholder="10-digit mobile number"
              maxLength={10}
              inputMode="numeric"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="customerAddress">Address *</Label>
            <Textarea
              id="customerAddress"
              name="customerAddress"
              value={formData.customerAddress}
              onChange={onInputChange}
              placeholder="Enter full address"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hypothecation">HYP (Hypothecation - If financed)</Label>
            <Input
              id="hypothecation"
              name="hypothecation"
              value={formData.hypothecation}
              onChange={onInputChange}
              placeholder="Finance company name if applicable"
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Vehicle Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Vehicle Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="vehicleModel">Vehicle Model *</Label>
            <Select
              value={formData.vehicleModel}
              onValueChange={(value) => onSelectChange('vehicleModel', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {vehicleModels.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vehicleColor">Color</Label>
            <Select
              value={formData.vehicleColor}
              onValueChange={(value) => onSelectChange('vehicleColor', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select color" />
              </SelectTrigger>
              <SelectContent>
                {vehicleColors.map((color) => (
                  <SelectItem key={color} value={color}>{color}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vehicleVariant">Variant (Optional)</Label>
            <Input
              id="vehicleVariant"
              name="vehicleVariant"
              value={formData.vehicleVariant}
              onChange={onInputChange}
              placeholder="Enter variant if any"
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Pricing - Matching the PDF format */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Pricing Details (Cost of New Three Wheeler)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="exShowroomPrice">Ex-Showroom Price *</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="exShowroomPrice"
                name="exShowroomPrice"
                value={formData.exShowroomPrice}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="rtoCharges">RTO Charges</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="rtoCharges"
                name="rtoCharges"
                value={formData.rtoCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="insuranceCharges">Insurance</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="insuranceCharges"
                name="insuranceCharges"
                value={formData.insuranceCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hpaCharges">HPA Charges</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="hpaCharges"
                name="hpaCharges"
                value={formData.hpaCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="handlingCharges">Handling/TP Charges</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="handlingCharges"
                name="handlingCharges"
                value={formData.handlingCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="accessoriesCharges">ACC (Accessories)</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="accessoriesCharges"
                name="accessoriesCharges"
                value={formData.accessoriesCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="otherCharges">Other Charges</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="otherCharges"
                name="otherCharges"
                value={formData.otherCharges}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="discount">Discount (–)</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="discount"
                name="discount"
                value={formData.discount}
                onChange={onInputChange}
                onKeyDown={handlePriceKeyDown}
                placeholder="0"
                className="pl-9 border-orange-400/60 focus-visible:ring-orange-400/40"
                inputMode="decimal"
              />
            </div>
          </div>
        </div>
        
        {/* Total */}
        <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-2">
          {parseFloat(formData.discount) > 0 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Subtotal (before discount)</span>
              <span>₹{formatCurrency(
                (parseFloat(formData.exShowroomPrice) || 0) +
                (parseFloat(formData.rtoCharges) || 0) +
                (parseFloat(formData.insuranceCharges) || 0) +
                (parseFloat(formData.hpaCharges) || 0) +
                (parseFloat(formData.handlingCharges) || 0) +
                (parseFloat(formData.accessoriesCharges) || 0) +
                (parseFloat(formData.otherCharges) || 0)
              )}</span>
            </div>
          )}
          {parseFloat(formData.discount) > 0 && (
            <div className="flex items-center justify-between text-sm text-orange-600 dark:text-orange-400">
              <span>Discount</span>
              <span>– ₹{formatCurrency(formData.discount)}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="font-heading font-semibold text-foreground">Grand Total</span>
            <span className="text-2xl font-bold text-primary">
              ₹{formatCurrency(calculateTotal())}
            </span>
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Additional Notes */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Additional Notes</h3>
        <div className="space-y-2">
          <Label htmlFor="notes">Notes (Optional - Will appear in Terms & Conditions)</Label>
          <Textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={onInputChange}
            placeholder="Any additional terms, conditions, or notes..."
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};

export default QuotationForm;
